import { Component } from '@theme/component'

/** @typedef {import('./product-story-overlay.js').ProductStoryOverlay} ProductStoryOverlay */

const STORY_ITEM = '.product-story__item'
const STORY_BUTTON = '.product-story__button'
const STORY_PREVIEW_INTERVAL = 3000

class ProductStory extends Component {
  /** @type {AbortController | null} */
  storyHoverController = null
  /** @type {number | null} */
  storyHoverIndex = null
  /** @type {MediaQueryList | null} */
  storyMotionQuery = null
  /** @type {HTMLElement[]} */
  storyPreviewItems = []
  storyPreviewIndex = 0
  storyPreviewTimer = null
  /** @type {ProductStoryOverlay | null} */
  overlay = null
  /** @type {Promise<ProductStoryOverlay> | null} */
  overlayRequest = null

  handleStoryVisibilityChange = () => {
    if (document.hidden) {
      this.pausePreviewForOverlay()
      return
    }
    this.resumePreviewAfterOverlay()
  }

  handleStoryMotionChange = () => {
    if (this.storyMotionQuery?.matches) {
      this.pausePreviewForOverlay()
      return
    }
    this.resumePreviewAfterOverlay()
  }

  connectedCallback() {
    super.connectedCallback()
    this.initStoryPreview()
    document.addEventListener(
      'visibilitychange',
      this.handleStoryVisibilityChange
    )
  }

  disconnectedCallback() {
    this.overlay?.destroy()
    this.overlay = null
    this.overlayRequest = null
    this.teardownStoryPreview()
    document.removeEventListener(
      'visibilitychange',
      this.handleStoryVisibilityChange
    )
    super.disconnectedCallback()
  }

  /**
   * @param {Event} event
   * @returns {Promise<void>}
   */
  async openStoryPanel(event) {
    event.preventDefault()
    const storyButton = event.target
    if (!(storyButton instanceof HTMLButtonElement)) {
      return
    }
    const overlay = await this.loadStoryOverlay()
    await overlay.openStoryButton(storyButton)
  }

  /** @returns {Promise<ProductStoryOverlay>} */
  loadStoryOverlay() {
    this.overlayRequest ??= this.importStoryOverlay()
    return this.overlayRequest
  }

  /** @returns {Promise<ProductStoryOverlay>} */
  async importStoryOverlay() {
    const overlaySource = this.dataset.overlaySrc
    if (!overlaySource) {
      throw new TypeError(
        `data-overlay-src resolved to ${overlaySource}, expected the product-story-overlay.js asset URL.`
      )
    }
    const { ProductStoryOverlay } = await import(overlaySource)
    this.overlay = new ProductStoryOverlay(this)
    return this.overlay
  }

  /** @returns {HTMLButtonElement[]} */
  getStoryButtons() {
    const storyButtons = Array.from(this.querySelectorAll(STORY_BUTTON))
    return storyButtons.filter(
      (storyButton) => storyButton instanceof HTMLButtonElement
    )
  }

  /**
   * @param {Element} storyScope
   * @returns {HTMLVideoElement | null}
   */
  getStoryVideo(storyScope) {
    const storyVideo = storyScope.querySelector('video')
    return storyVideo instanceof HTMLVideoElement ? storyVideo : null
  }

  /** @param {Element} storyScope */
  hydrateStoryVideo(storyScope) {
    const deferredVideo = storyScope.querySelector('ui-video')
    if (deferredVideo instanceof HTMLElement && 'play' in deferredVideo) {
      deferredVideo.play()
    }
  }

  /** @param {HTMLVideoElement} storyVideo */
  playStoryVideo(storyVideo) {
    const playRequest = storyVideo.play()
    if (playRequest instanceof Promise) {
      playRequest.catch(() => undefined)
    }
  }

  initStoryPreview() {
    this.storyPreviewItems = Array.from(this.querySelectorAll(STORY_ITEM))
    if (!this.storyPreviewItems.length) {
      return
    }
    this.storyMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    this.storyMotionQuery.addEventListener(
      'change',
      this.handleStoryMotionChange
    )
    this.bindStoryHover()
    this.activateStoryPreviewAt(0)
    this.startStoryPreview()
  }

  bindStoryHover() {
    if (!window.matchMedia('(hover: hover)').matches) {
      return
    }
    this.storyHoverController = new AbortController()
    const { signal } = this.storyHoverController
    this.storyPreviewItems.forEach((storyItem, index) => {
      storyItem.addEventListener(
        'mouseenter',
        () => this.onStoryHoverEnter(index),
        { signal }
      )
      storyItem.addEventListener(
        'mouseleave',
        () => this.onStoryHoverLeave(index),
        { signal }
      )
    })
  }

  /** @param {number} index */
  onStoryHoverEnter(index) {
    if (this.overlay?.isOpen || this.storyMotionQuery?.matches) {
      return
    }
    this.storyHoverIndex = index
    this.activateStoryPreviewAt(index)
    this.startStoryPreview()
  }

  /** @param {number} index */
  onStoryHoverLeave(index) {
    if (this.storyHoverIndex !== index) {
      return
    }
    this.storyHoverIndex = null
  }

  teardownStoryPreview() {
    this.stopStoryPreview()
    this.pauseStoryPreviewVideos()
    this.storyHoverController?.abort()
    this.storyHoverController = null
    this.storyHoverIndex = null
    this.storyMotionQuery?.removeEventListener(
      'change',
      this.handleStoryMotionChange
    )
    this.storyMotionQuery = null
    this.storyPreviewItems = []
    this.storyPreviewIndex = 0
  }

  startStoryPreview() {
    if (!this.storyPreviewItems.length || this.overlay?.isOpen) {
      return
    }
    if (this.storyMotionQuery?.matches) {
      this.pauseStoryPreviewVideos()
      return
    }
    this.stopStoryPreview()
    this.storyPreviewTimer = window.setInterval(
      () => this.advanceStoryPreview(),
      STORY_PREVIEW_INTERVAL
    )
  }

  stopStoryPreview() {
    if (!this.storyPreviewTimer) {
      return
    }
    window.clearInterval(this.storyPreviewTimer)
    this.storyPreviewTimer = null
  }

  advanceStoryPreview() {
    if (!this.storyPreviewItems.length) {
      return
    }
    if (this.storyHoverIndex !== null) {
      this.activateStoryPreviewAt(this.storyHoverIndex)
      return
    }
    this.activateStoryPreviewAt(this.getNextStoryPreviewIndex())
  }

  /** @returns {number} */
  getNextStoryPreviewIndex() {
    const total = this.storyPreviewItems.length
    return total === 1 ? 0 : (this.storyPreviewIndex + 1) % total
  }

  /** @param {number} index */
  activateStoryPreviewAt(index) {
    this.storyPreviewIndex = index
    this.storyPreviewItems.forEach((storyItem, itemIndex) => {
      const storyVideo = this.getStoryVideo(storyItem)
      if (!storyVideo) {
        return
      }
      this.setStoryPreviewPlayback(storyVideo, itemIndex === index)
    })
  }

  /**
   * @param {HTMLVideoElement} storyVideo
   * @param {boolean} shouldPlay
   */
  setStoryPreviewPlayback(storyVideo, shouldPlay) {
    if (!shouldPlay) {
      storyVideo.pause()
      return
    }
    storyVideo.currentTime = 0
    this.playStoryVideo(storyVideo)
  }

  pauseStoryPreviewVideos() {
    for (const storyItem of this.storyPreviewItems) {
      this.getStoryVideo(storyItem)?.pause()
    }
  }

  pausePreviewForOverlay() {
    this.stopStoryPreview()
    this.pauseStoryPreviewVideos()
    this.storyHoverIndex = null
  }

  resumePreviewAfterOverlay() {
    if (document.hidden || !this.storyPreviewItems.length) {
      return
    }
    if (this.storyMotionQuery?.matches || this.overlay?.isOpen) {
      return
    }
    this.activateStoryPreviewAt(this.storyPreviewIndex)
    this.startStoryPreview()
  }
}

if (!customElements.get('product-story')) {
  customElements.define('product-story', ProductStory)
}
